/**
 * /equipment/pressure-control/tests/:testId — one recorded pressure test
 * (api#1281, BOP-2). Panels stack full width: the test, its components, the
 * gauge as it stood when the test was recorded, and the chart photo.
 *
 * APPEND-ONLY: no Edit and no Delete. The chart is a `pressure_control_chart`
 * upload, which the API will not delete, so it offers View only.
 *
 * THE GAUGE PANEL IS A SNAPSHOT. calibratedOn / dueOn / range are what the
 * API copied onto the test when it was recorded, so a calibration entered
 * later does not change what this test was read against.
 */
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { FileText, Loader2 } from 'lucide-react';
import { openUploadDownload } from '../../api/client';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { PcBadge } from '../../components/pressure-control/PcSectionNav';
import { usePcTest } from '../../hooks/usePressureControl';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { formatInstant } from '../../lib/calendar-date';
import { isNotFound, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  PC_LIST,
  PC_RESULT_FILL,
  PC_RESULT_LABEL,
  PC_TESTS,
  pcClassLabel,
  pcDay,
  pcMinutes,
  pcPressure,
  pcTester,
  pcTestKindLabel,
} from '../../lib/pressure-control';
import { DhEmpty, DhPanel, DhSpec } from '../downhole/DownholeBits';
import { useBackToList } from '../downhole/recordPage';

const text = (v: string | null | undefined) => (v && v.trim() ? v : '—');

/** "35,000 kPa" plus, when it was typed in another unit, "(entered 5,076 psi)". */
function pressureCell(value: number | null, unit: string, entered: number | null, enteredUnit: string | null) {
  const main = pcPressure(value, unit);
  if (entered === null || !enteredUnit || enteredUnit === unit) return main;
  return `${main} (entered ${pcPressure(entered, enteredUnit)})`;
}

export default function PressureControlTestDetailScreen() {
  const { testId } = useParams<{ testId: string }>();
  const back = useBackToList(PC_TESTS);
  const tz = useTenantTimeZone();
  const query = usePcTest(testId);
  const failure = queryLoadFailure(query);
  const t = query.data ?? null;

  return (
    <div data-testid="pc-test-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Pressure test"
        title={t ? `${pcTestKindLabel(t.testKind)} test · ${pcDay(t.testedOn)}` : query.isLoading ? 'Loading…' : 'Pressure test'}
        onBack={back}
        backLabel="Back"
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading pressure test" />
            </div>
          ) : (failure && isNotFound(queryFailureError(query))) || (!failure && !t) ? (
            <DhPanel eyebrow="Pressure test" title="Not found" testId="pc-test-not-found">
              <DhEmpty>
                No such pressure test on this account.{' '}
                <Link to={PC_TESTS} className="font-semibold underline underline-offset-2">Back to the tests</Link>
              </DhEmpty>
            </DhPanel>
          ) : failure ? (
            <DhPanel eyebrow="Pressure test" title="Test" testId="pc-test-load-failure">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this pressure test" />
            </DhPanel>
          ) : t ? (
            <>
              <DhPanel eyebrow="Pressure test" title="The test" testId="pc-test-summary">
                <dl className="grid grid-cols-2 gap-4 px-4 py-4 md:grid-cols-4">
                  <DhSpec label="Result" value={<PcBadge value={t.result} labels={PC_RESULT_LABEL} fills={PC_RESULT_FILL} testId="pc-test-result" />} />
                  <DhSpec label="Tested at" value={formatInstant(t.testedAt, tz) ?? '—'} />
                  <DhSpec label="Kind" value={pcTestKindLabel(t.testKind)} />
                  <DhSpec label="Stack" value={text(t.stackName)} />
                  <DhSpec label="Tested by" value={pcTester(t)} />
                  <DhSpec label="Witnessed by" value={text(t.witnessedBy)} />
                  <DhSpec label="Reference" value={text(t.contextRef)} />
                  <DhSpec label="Recorded" value={formatInstant(t.createdAt, tz) ?? '—'} />
                </dl>
                {t.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{t.notes}</p>}
              </DhPanel>

              <DhPanel
                eyebrow={t.pressureUnit ? `Pressures in ${t.pressureUnit}` : 'Pressures'}
                title="Components tested"
                testId="pc-test-items-panel"
                footer={`${t.items.length} ${t.items.length === 1 ? 'component' : 'components'}`}
              >
                {t.items.length === 0 ? (
                  <DhEmpty>No component is listed on this test.</DhEmpty>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[900px] text-left">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                        <tr>
                          <th scope="col" className="px-4 py-3">Component</th>
                          <th scope="col" className="px-4 py-3">Low test</th>
                          <th scope="col" className="px-4 py-3">Low hold</th>
                          <th scope="col" className="px-4 py-3">High test</th>
                          <th scope="col" className="px-4 py-3">High hold</th>
                          <th scope="col" className="px-4 py-3">Result</th>
                          <th scope="col" className="px-4 py-3">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {t.items.map((i) => (
                          <tr key={i.id ?? i.equipmentId} className="border-t border-fieldiq-border" data-testid="pc-test-item-row">
                            <td className="px-4 py-3 text-sm">
                              <Link to={`${PC_LIST}/${i.equipmentId}`} className="font-semibold text-white underline-offset-2 hover:underline">
                                {i.name ?? '—'}
                              </Link>
                              <span className="block text-xs text-fieldiq-muted">{pcClassLabel(i.componentClass)}</span>
                            </td>
                            <td className="px-4 py-3 font-mono text-sm tabular-nums text-fieldiq-text">
                              {pressureCell(i.lowTestPressure, t.pressureUnit, i.lowTestEntered, i.lowTestUnit)}
                            </td>
                            <td className="px-4 py-3 font-mono text-sm tabular-nums text-fieldiq-text">{pcMinutes(i.lowHoldMinutes)}</td>
                            <td className="px-4 py-3 font-mono text-sm tabular-nums text-fieldiq-text">
                              {pressureCell(i.highTestPressure, t.pressureUnit, i.highTestEntered, i.highTestUnit)}
                            </td>
                            <td className="px-4 py-3 font-mono text-sm tabular-nums text-fieldiq-text">{pcMinutes(i.highHoldMinutes)}</td>
                            <td className="px-4 py-3"><PcBadge value={i.result} labels={PC_RESULT_LABEL} fills={PC_RESULT_FILL} /></td>
                            <td className="px-4 py-3 text-sm text-fieldiq-text">{text(i.notes)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </DhPanel>

              <DhPanel eyebrow="As recorded with the test" title="Gauge" testId="pc-test-gauge">
                <dl className="grid grid-cols-2 gap-4 px-4 py-4 md:grid-cols-4">
                  <DhSpec
                    label="Gauge"
                    value={t.gauge.equipmentId ? (
                      <Link to={`${PC_LIST}/calibrations/${t.gauge.equipmentId}`} className="underline-offset-2 hover:underline">{t.gauge.name ?? '—'}</Link>
                    ) : '—'}
                  />
                  <DhSpec label="Calibrated" value={pcDay(t.gauge.calibratedOn)} />
                  <DhSpec label="Valid through" value={pcDay(t.gauge.dueOn)} />
                  <DhSpec label="Calibrated range" mono value={pcPressure(t.gauge.rangeMax, t.pressureUnit)} />
                </dl>
              </DhPanel>

              <DhPanel eyebrow="Evidence" title="Chart photo" testId="pc-test-chart">
                {t.chartUploadId ? (
                  <div className="flex flex-wrap items-center gap-3 px-4 py-4">
                    <button
                      type="button"
                      onClick={() => void openUploadDownload(t.chartUploadId!)}
                      className="inline-flex items-center gap-1 text-sm font-semibold text-fieldiq-text underline-offset-2 hover:underline"
                    >
                      <FileText className="size-4" aria-hidden="true" />
                      View chart
                    </button>
                    <span className="text-xs text-fieldiq-muted">A test&rsquo;s chart is kept with it and cannot be removed.</span>
                  </div>
                ) : (
                  <DhEmpty>No chart photo was attached to this test.</DhEmpty>
                )}
              </DhPanel>
            </>
          ) : null}
        </div>
      </section>
    </div>
  );
}
